import { css } from '@emotion/react'
import { isEmpty, VFlow } from 'bold-ui'
import { RadioOption, resolveName, SimNaoEnum } from 'components/form'
import { SimNaoSwitchButtonField } from 'components/form/field/SimNaoSwitchButtonField'
import { MChatPerguntaEnum } from 'graphql/types.generated'
import { useField } from 'react-final-form'
import { MetaPath } from 'util/metaPath'

import { ConsultaSeguimentoArrowDown } from '../../components/ConsultaSeguimentoArrowDown'
import { ConsultaSeguimentoIndicatorHelper } from '../../components/ConsultaSeguimentoIndicatorHelper'
import { ConsultaSeguimentoIndicatorResultado } from '../../components/ConsultaSeguimentoIndicatorResultado'
import { ConsultaSeguimentoIndicatorStart } from '../../components/ConsultaSeguimentoIndicatorStart'
import { ConsultaSeguimentoPerguntaHeader } from '../../components/ConsultaSeguimentoPerguntaHeader'
import { ConsultaSeguimentoPerguntaHeaderTitle } from '../../components/ConsultaSeguimentoPerguntaHeaderTitle'
import { PerguntaSimNaoRadioGroupBoxField } from '../../components/PerguntaSimNaoRadioGroupBoxField'
import { PerguntaSimNaoRadioListField } from '../../components/PerguntaSimNaoRadioListField'
import { isPerguntaStatusRespondendo } from '../../consulta-seguimento-aplicacao/utils-consultaSeguimentoAplicacao'
import { useConsultaSeguimentoSetResultado } from '../../hooks/useConsultaSeguimentoSetResultado'
import {
  ConsultaSeguimentoPergunta20ExemplosEnum,
  ConsultaSeguimentoPergunta20Model,
} from './model-consultaSeguimentoPergunta20'
import {
  calculateResultadoPergunta20,
  conditionToCalculatePergunta20,
  hasPergunta20ExemplosAnySim,
} from './utils-consultaSeguimentoPergunta20'

interface ConsultaSeguimentoPergunta20FieldProps {
  name: MetaPath<ConsultaSeguimentoPergunta20Model>
}

export function ConsultaSeguimentoPergunta20Field(props: Readonly<ConsultaSeguimentoPergunta20FieldProps>) {
  const { name } = props

  const {
    input: { value },
  } = useField<ConsultaSeguimentoPergunta20Model>(resolveName(name), {
    subscription: { value: true },
  })

  useConsultaSeguimentoSetResultado<ConsultaSeguimentoPergunta20Model>({
    name,
    conditionToCalculate: conditionToCalculatePergunta20,
    calculateResultado: calculateResultadoPergunta20,
  })

  const hasExemplosAnySim = hasPergunta20ExemplosAnySim(value)
  const isPerguntaHeaderSim = value.PERGUNTA_20_HEADER === SimNaoEnum.SIM
  const isPerguntaHeaderNao = value.PERGUNTA_20_HEADER === SimNaoEnum.NAO
  const isGostaDePularOuBalancarNao = value.PERGUNTA_20_GOSTA_DE_PULAR_OU_BALANCAR === SimNaoEnum.NAO

  const textResultadoPassou =
    hasExemplosAnySim && (isPerguntaHeaderNao || (isPerguntaHeaderSim && isGostaDePularOuBalancarNao))
      ? 'Sim para qualquer exemplo específico (ou se “outro” é uma resposta positiva):'
      : ''

  return (
    <VFlow vSpacing={0}>
      <VFlow>
        {isPerguntaStatusRespondendo(value.status) && (
          <ConsultaSeguimentoPerguntaHeader
            pergunta={MChatPerguntaEnum.PERGUNTA_20}
            title={<ConsultaSeguimentoPerguntaHeaderTitle sufix=' gosta de atividades de movimento?' />}
            status={value.status}
            field={<SimNaoSwitchButtonField name={name.PERGUNTA_20_HEADER} />}
          />
        )}
        {!isEmpty(value.PERGUNTA_20_HEADER) && (
          <VFlow vSpacing={0}>
            <ConsultaSeguimentoIndicatorStart />
            {value.PERGUNTA_20_HEADER === SimNaoEnum.SIM && (
              <PerguntaSimNaoRadioGroupBoxField
                name={name.PERGUNTA_20_GOSTA_DE_PULAR_OU_BALANCAR}
                label='Ele gosta quando alguém o faz pular ou balançar?'
                required
              />
            )}
            {(value.PERGUNTA_20_HEADER === SimNaoEnum.NAO || isGostaDePularOuBalancarNao) && (
              <VFlow vSpacing={0}>
                {value.PERGUNTA_20_HEADER === SimNaoEnum.SIM && <ConsultaSeguimentoArrowDown />}
                <VFlow>
                  <ConsultaSeguimentoIndicatorHelper
                    title='Quando você balança ou joga ele para o alto, como ele reage?'
                    subtitle='(Se o genitor não der um dos exemplos abaixo, perguntar cada um deles individualmente.)'
                  />
                  <PerguntaSimNaoRadioListField<ConsultaSeguimentoPergunta20ExemplosEnum>
                    name={name.PERGUNTA_20_EXEMPLOS}
                    nameOutro={name.PERGUNTA_20_OUTRO}
                    label='O seu filho...'
                    description='Responda os exemplos até pontuar “Passou” ou “Falhou”'
                    options={PERGUNTA_20_EXEMPLOS_OPTIONS}
                    hideOutrosFieldRadioOptions
                    required
                  />
                </VFlow>
              </VFlow>
            )}
          </VFlow>
        )}
      </VFlow>
      {value.resultado && (
        <ConsultaSeguimentoIndicatorResultado
          resultado={value.resultado}
          text={{
            FALHOU: 'Não para todos:',
            PASSOU: textResultadoPassou,
          }}
          style={styles.resultado}
        />
      )}
    </VFlow>
  )
}

const PERGUNTA_20_EXEMPLOS_OPTIONS: RadioOption<ConsultaSeguimentoPergunta20ExemplosEnum>[] = [
  {
    label: 'Ri ou dá gargalhadas?',
    value: ConsultaSeguimentoPergunta20ExemplosEnum.RI_OU_GARGALHA,
  },
  {
    label: 'Fala ou emite algum som?',
    value: ConsultaSeguimentoPergunta20ExemplosEnum.FALA_OU_EMITE_SOM,
  },
  {
    label: 'Pede mais, esticando seus braços?',
    value: ConsultaSeguimentoPergunta20ExemplosEnum.PEDE_MAIS,
  },
]

const styles = {
  resultado: css`
    max-width: 17rem;
  `,
}
